import React from 'react';
import { useSelector } from 'react-redux';
import { ArrayHelpers, DomHelpers, Help, Paging, SystemText } from 'e4shared';
import BlobImage from 'components/blobImage';
import { getBadge } from 'data/badges';
import { apiGet } from 'services';
import { isToday, isYesterday, e4Format, e4Formats } from 'helpers/dates';
import { hasModule } from 'helpers/profile';

import './latestBadges.css';
import WidgetBase from 'views/myDashboard/components/widgets/_base';

export const LatestBadges = ({ item }) => {
    const loaded = React.useRef(null);
    const [loading, setLoading] = React.useState(true);
    const [data, setData] = React.useState([]);
    const [pagedData, setPagedData] = React.useState([]);
    const [page, setPage] = React.useState(1);
    const [pageSize, setPageSize] = React.useState(6);
    const recordCount = data.length;
    const rowHeight = 75;
    const extrasHeight = 60;

    const user = useSelector(state => state.user.user);

    const gotResponse = response => {
        setLoading(false);

        const filtered = response.data.filter(b => {
            const badge = b.type ? getBadge(b.type) : b;
            if (!badge) {
                return false;
            }

            return !(badge.module && !hasModule(user, badge.module));
        });

        setData(filtered);
    };

    React.useEffect(() => {
        !loading && loaded.current && DomHelpers.truncateText('widget-lb-title', loaded.current, 100);
    }, [loading, loaded.current, item.w, page]);

    React.useEffect(() => {
        apiGet(`dashboard/badge-feed`, user.guid, response => gotResponse(response));
    }, [user.guid]);

    const text = {
        today: SystemText('Home/Widgets/Badges/Today'),
        yesterday: SystemText('Home/Widgets/Badges/Yesterday'),
        none: SystemText('Home/Widgets/Badges/None'),
        howTo: SystemText('Home/Widgets/Badges/HowTo'),
    };

    const when = d => {
        if (isToday(d)) return text.today;
        if (isYesterday(d)) return text.yesterday;
        return `on ${e4Format(d, e4Formats.recent)}`;
    };

    React.useEffect(() => {
        setPagedData(ArrayHelpers.getPage(data, page, pageSize));
    }, [page]);

    React.useEffect(() => {
        const pageSizeGenerator = () => {
            const thisHeight = 110 * item.h - 50;
            let result = Math.ceil((thisHeight - extrasHeight) / rowHeight, 0);
            if (result < recordCount) result--;
            return result < 1 ? 1 : result;
        };

        const ps = pageSizeGenerator();
        setPagedData(ArrayHelpers.getPage(data, 1, ps));
        setPage(1);
        setPageSize(ps);
    }, [loaded.current, data, item.h, recordCount]);

    const chart = (
        <div ref={loaded}>
            {data.length === 0 ? (
                <div>
                    <p>{text.none}</p>
                </div>
            ) : (
                <div>
                    {pagedData.map((b, i) => {
                        const badge = b.type ? getBadge(b.type) : { ...b, colour: 'gold' };
                        if (badge.module && !hasModule(user, badge.module)) return null;
                        return (
                            <div
                                key={`badge-${page}-${b.profile_Id}-${badge.type}`}
                                className="badge-info flex flex-center pt10 pb10"
                                style={{ height: `${rowHeight}px`, borderBottom: 'solid 1px #ebebeb' }}
                            >
                                <div className="home-badge">
                                    <i
                                        style={badge.type ? null : { visibility: 'hidden', marginBottom: '-25px' }}
                                        className={`fa fa-certificate ${badge.colour}`}
                                    />
                                    <div>
                                        {badge.type ? (
                                            <i className={`fa fa-${badge.icon}  ${badge.colour}`} />
                                        ) : (
                                            <BlobImage
                                                folder="badge_Icon"
                                                src={badge.icon}
                                                ratio={1}
                                                alt={badge.description}
                                                className="badge-icon"
                                            />
                                        )}
                                    </div>
                                </div>
                                <div>
                                    <div className="flex flex-center">
                                        <label
                                            style={{ fontSize: '12px' }}
                                            className="label label-default ellipse widget-lb-title"
                                            title={`${badge.type ? `${text.howTo} - ` : ''}${badge.description}`}
                                        >
                                            {badge.name}
                                        </label>
                                        {badge.description && (
                                            <div className="mlm5 inline" style={{ position: 'relative', top: '-2px' }}>
                                                <Help id={`badge-help-${page}-${i}`}>{badge.description}</Help>
                                            </div>
                                        )}
                                    </div>
                                    <div className="flex">
                                        <div className="user">
                                            <BlobImage folder="avatar" src={b.avatar} />
                                        </div>
                                        <div className="flex-grow">
                                            <button
                                                className="btn btn-link"
                                                onClick={() => item.setShowBadges(b.profile_Id)}
                                            >
                                                {b.full_Name}
                                            </button>
                                            <div>{when(b.awarded)}</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                    {recordCount > pageSize && (
                        <div className="text-center mt10">
                            <Paging
                                page={page}
                                narrow={item.w < 3}
                                pageSize={pageSize}
                                recordCount={recordCount}
                                goToPage={setPage}
                            />
                        </div>
                    )}
                </div>
            )}
        </div>
    );

    return (
        <WidgetBase
            filters={null}
            chart={chart}
            item={item}
            id={'latest-badges'}
            help={null}
            hideConfig
            extra={null}
            loading={loading}
        />
    );
};

export default LatestBadges;
